import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../../api/client";

// TODO: translate admin panel once Phase 2 ships broader i18n coverage.

interface ValidationError {
  row: number;
  field: string;
  value: string;
  error: string;
}

interface ValidationWarning {
  row: number;
  field: string;
  value: string;
  warning: string;
}

interface PreviewRow {
  row: number;
  first_name: string;
  last_name: string;
  email: string;
  site: string;
  department: string;
  status: string;
}

interface ValidationResponse {
  token: string;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  preview: PreviewRow[];
}

interface CreatedEmployee {
  employee_number: string;
  name: string;
  email: string;
  initial_pin: string;
}

interface ConfirmResponse {
  batch_id: string;
  created: number;
  skipped: number;
  employees: CreatedEmployee[];
}

type Stage = "upload" | "review" | "done";

export default function BulkUploadPage() {
  const [stage, setStage] = useState<Stage>("upload");
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [result, setResult] = useState<ConfirmResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onFile = async (file: File) => {
    setError(null);
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch("/api/employees/bulk-upload", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const body = await res.json();
      if (!res.ok) {
        setError(String(body?.detail ?? "Upload failed"));
        return;
      }
      setValidation(body);
      setStage("review");
    } catch (err) {
      setError((err as Error).message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = async (mode: "all" | "skip_errors") => {
    if (!validation) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<ConfirmResponse>(
        "/api/employees/bulk-upload/confirm",
        { token: validation.token, mode },
      );
      setResult(res);
      setStage("done");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Bulk Upload</h1>
          <p className="text-sm text-gray-500">
            Import many employees at once from an XLSX or CSV file.
          </p>
        </div>
        <Link
          to="/admin/employees"
          className="text-sm text-gray-500 hover:underline"
        >
          ← Back to employees
        </Link>
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      {stage === "upload" && (
        <UploadStage onFile={onFile} submitting={submitting} />
      )}
      {stage === "review" && validation && (
        <ReviewStage
          data={validation}
          submitting={submitting}
          onConfirm={confirm}
          onRestart={() => {
            setValidation(null);
            setStage("upload");
          }}
        />
      )}
      {stage === "done" && result && <DoneStage result={result} />}
    </div>
  );
}

function UploadStage({
  onFile,
  submitting,
}: {
  onFile: (file: File) => Promise<void>;
  submitting: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-gray-900">1. Download the template</h2>
        <p className="mt-1 text-sm text-gray-500">
          The template has one example row (grey italics). Delete the example and
          fill in your employees below it.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href="/api/employees/template?format=xlsx"
            className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
          >
            Download XLSX Template
          </a>
          <a
            href="/api/employees/template?format=csv"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            Download CSV Template
          </a>
        </div>
      </section>

      <section
        className={[
          "rounded-xl border-2 border-dashed bg-white p-8 text-center",
          dragOver ? "border-pme-blue bg-pme-light-blue/10" : "border-gray-300",
        ].join(" ")}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) void onFile(file);
        }}
      >
        <h2 className="text-sm font-semibold text-gray-900">
          2. Upload your completed file
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Drag &amp; drop here, or
        </p>
        <label className="mt-4 inline-block">
          <span className="cursor-pointer rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">
            {submitting ? "Validating..." : "Choose File"}
          </span>
          <input
            type="file"
            accept=".xlsx,.csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
            }}
          />
        </label>
        <p className="mt-3 text-xs text-gray-400">
          Accepted formats: .xlsx, .csv. Max 5 MB.
        </p>
      </section>
    </div>
  );
}

function ReviewStage({
  data,
  submitting,
  onConfirm,
  onRestart,
}: {
  data: ValidationResponse;
  submitting: boolean;
  onConfirm: (mode: "all" | "skip_errors") => Promise<void>;
  onRestart: () => void;
}) {
  const hasErrors = data.error_rows > 0;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <Card label="Total rows" value={data.total_rows} tone="gray" />
        <Card label="Valid" value={data.valid_rows} tone="green" />
        <Card label="Errors" value={data.error_rows} tone={hasErrors ? "red" : "gray"} />
      </div>

      {data.warnings.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-amber-50/40 p-4">
          <h3 className="text-sm font-semibold text-amber-900">Warnings</h3>
          <ul className="mt-2 space-y-1 text-xs text-amber-900">
            {data.warnings.map((w, i) => (
              <li key={i}>
                Row {w.row}: {w.warning}
              </li>
            ))}
          </ul>
        </section>
      )}

      {hasErrors && (
        <section className="rounded-xl border border-red-200 bg-white">
          <header className="border-b border-red-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-red-800">Errors</h3>
          </header>
          <table className="min-w-full text-sm">
            <thead className="bg-red-50 text-left text-xs uppercase tracking-wide text-red-700">
              <tr>
                <th className="px-3 py-2">Row</th>
                <th className="px-3 py-2">Field</th>
                <th className="px-3 py-2">Value</th>
                <th className="px-3 py-2">Problem</th>
              </tr>
            </thead>
            <tbody>
              {data.errors.map((e, i) => (
                <tr key={i} className="odd:bg-red-50/40">
                  <td className="px-3 py-2 font-mono">{e.row}</td>
                  <td className="px-3 py-2">{e.field}</td>
                  <td className="px-3 py-2 text-gray-600">{e.value || "—"}</td>
                  <td className="px-3 py-2 text-red-700">{e.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {data.valid_rows > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white">
          <header className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Preview (first 10)</h3>
          </header>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Site</th>
                <th className="px-3 py-2">Department</th>
              </tr>
            </thead>
            <tbody>
              {data.preview.map((p) => (
                <tr key={p.row} className="odd:bg-gray-50/40">
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {p.first_name} {p.last_name}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{p.email}</td>
                  <td className="px-3 py-2">{p.site}</td>
                  <td className="px-3 py-2">{p.department}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onRestart}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
        >
          Fix and Re-upload
        </button>
        {hasErrors && data.valid_rows > 0 && (
          <button
            type="button"
            onClick={() => void onConfirm("skip_errors")}
            disabled={submitting}
            className="rounded-lg border border-amber-400 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 disabled:opacity-60"
          >
            Import Valid Rows Only ({data.valid_rows})
          </button>
        )}
        {!hasErrors && data.valid_rows > 0 && (
          <button
            type="button"
            onClick={() => void onConfirm("all")}
            disabled={submitting}
            className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            Import All {data.valid_rows} Employees
          </button>
        )}
      </div>
    </div>
  );
}

function DoneStage({ result }: { result: ConfirmResponse }) {
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-800">
        {result.created} employees created successfully.
      </div>

      <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
        These PINs will only be available for 1 hour. Download the sheet or note
        them now — once the cache expires they cannot be shown again.
      </div>

      <div className="flex flex-wrap gap-2">
        <a
          href={`/api/employees/bulk-upload/${result.batch_id}/pin-sheet?format=xlsx`}
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
        >
          Download PIN Sheet (XLSX)
        </a>
        <Link
          to="/admin/employees"
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
        >
          Done
        </Link>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white">
        <header className="border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Initial PINs</h3>
        </header>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Number</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Initial PIN</th>
            </tr>
          </thead>
          <tbody>
            {result.employees.map((e) => (
              <tr key={e.employee_number} className="odd:bg-gray-50/40">
                <td className="px-3 py-2 font-mono">{e.employee_number}</td>
                <td className="px-3 py-2 font-medium text-gray-900">{e.name}</td>
                <td className="px-3 py-2 text-gray-600">{e.email}</td>
                <td className="px-3 py-2 font-mono tracking-widest">
                  {e.initial_pin}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Card({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "green" | "red" | "gray";
}) {
  const cls = {
    green: "bg-green-50 text-green-700",
    red: "bg-red-50 text-red-700",
    gray: "bg-gray-50 text-gray-700",
  }[tone];
  return (
    <div className={["rounded-xl p-4", cls].join(" ")}>
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-3xl font-semibold">{value}</p>
    </div>
  );
}
